import type { FinvizTab, FinvizFilterField, FinvizOption, FinvizTabId } from './types';

// Standard finviz.com options with "Any" and "Custom" support
// Note: Uses fs_* prefix to match finviz.com DOM naming exactly
const createOptions = (items: string[]): FinvizOption[] => [
  { value: '', label: 'Any' },
  ...items.map(item => ({ value: item, label: item })),
  { value: 'CUSTOM', label: 'Custom' },
];

// Performance percentage options (finviz uses "Up x%" and "Down x%" format)
const performanceOptions: FinvizOption[] = [
  { value: '', label: 'Any' },
  { value: 'Up 1%', label: 'Up 1%' },
  { value: 'Up 2%', label: 'Up 2%' },
  { value: 'Up 3%', label: 'Up 3%' },
  { value: 'Up 4%', label: 'Up 4%' },
  { value: 'Up 5%', label: 'Up 5%' },
  { value: 'Up 7%', label: 'Up 7%' },
  { value: 'Up 10%', label: 'Up 10%' },
  { value: 'Up 15%', label: 'Up 15%' },
  { value: 'Up 20%', label: 'Up 20%' },
  { value: 'Up 25%', label: 'Up 25%' },
  { value: 'Up 30%', label: 'Up 30%' },
  { value: 'Up 50%', label: 'Up 50%' },
  { value: 'Up 100%', label: 'Up 100%' },
  { value: 'Down 1%', label: 'Down 1%' },
  { value: 'Down 2%', label: 'Down 2%' },
  { value: 'Down 3%', label: 'Down 3%' },
  { value: 'Down 4%', label: 'Down 4%' },
  { value: 'Down 5%', label: 'Down 5%' },
  { value: 'Down 7%', label: 'Down 7%' },
  { value: 'Down 10%', label: 'Down 10%' },
  { value: 'Down 15%', label: 'Down 15%' },
  { value: 'Down 20%', label: 'Down 20%' },
  { value: 'Down 25%', label: 'Down 25%' },
  { value: 'Down 30%', label: 'Down 30%' },
  { value: 'Down 50%', label: 'Down 50%' },
  { value: 'CUSTOM', label: 'Custom' },
];

// Tab definitions
export const FINVIZ_TABS: FinvizTab[] = [
  { id: 'descriptive', label: 'Descriptive' },
  { id: 'valuation', label: 'Valuation' },
  { id: 'ownership', label: 'Ownership' },
  { id: 'performance', label: 'Performance' },
  { id: 'technical', label: 'Technical' },
  { id: 'etf', label: 'ETF' },
];

// Descriptive Tab Filters (fs_* prefix matches finviz.com DOM)
export const descriptiveFilters: FinvizFilterField[] = [
  {
    id: 'fs_exch',
    label: 'Exchange',
    type: 'select',
    options: createOptions(['NASDAQ', 'NYSE', 'AMEX']),
    allowCustom: true,
    customPlaceholder: 'e.g., NASDAQ, NYSE, AMEX',
  },
  {
    id: 'fs_idx',
    label: 'Index',
    type: 'select',
    options: createOptions(['S&P 500', 'NASDAQ 100', 'Dow 30']),
    allowCustom: true,
    customPlaceholder: 'e.g., S&P 500, NASDAQ 100',
  },
  {
    id: 'fs_sec',
    label: 'Sector',
    type: 'select',
    options: createOptions([
      'Technology',
      'Healthcare',
      'Financial',
      'Consumer Cyclical',
      'Consumer Defensive',
      'Industrials',
      'Energy',
      'Utilities',
      'Materials',
      'Real Estate',
      'Communication Services',
    ]),
    allowCustom: true,
    customPlaceholder: 'e.g., Technology, Healthcare',
  },
  {
    id: 'fs_ind',
    label: 'Industry',
    type: 'select',
    options: createOptions([
      'Semiconductors',
      'Software Infrastructure',
      'Hardware',
      'Medical Devices',
      'Biotechnology',
      'Pharmaceuticals',
      'Banks',
      'Insurance',
      'Retail',
      'Restaurants',
      'Aerospace',
      'Oil & Gas',
      'Utilities',
      'Metals & Mining',
    ]),
    allowCustom: true,
    customPlaceholder: 'e.g., Semiconductors, Software',
    note: '154 industries available - may need dynamic loading',
  },
  {
    id: 'fs_geo',
    label: 'Country',
    type: 'select',
    options: createOptions([
      'USA',
      'China',
      'Germany',
      'UK',
      'Japan',
      'France',
      'Canada',
      'Australia',
      'India',
      'Taiwan',
      'South Korea',
      'Netherlands',
      'Switzerland',
      'Brazil',
    ]),
    allowCustom: true,
    note: '64 countries available',
  },
  {
    id: 'fs_cap',
    label: 'Market Cap',
    type: 'select',
    options: createOptions([
      'Mega',
      'Large',
      'Mid',
      'Small',
      'Micro',
      'Nano',
    ]),
    allowCustom: true,
  },
];

// Valuation Tab Filters (fs_fa_* prefix for Financial metrics)
export const valuationFilters: FinvizFilterField[] = [
  {
    id: 'fs_fa_pe',
    label: 'P/E Ratio',
    type: 'select',
    options: createOptions([
      'Under 5', 'Under 10', 'Under 15', 'Under 20', 'Under 25', 'Under 30', 'Under 35', 'Under 40', 'Under 45', 'Under 50',
      'Over 5', 'Over 10', 'Over 15', 'Over 20', 'Over 25', 'Over 30', 'Over 35', 'Over 40', 'Over 45', 'Over 50',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter P/E ratio (e.g., Under 20)',
    validation: 'number',
  },
  {
    id: 'fs_fa_pc',
    label: 'Price/Cash Ratio',
    type: 'select',
    options: createOptions([
      'Under 1', 'Under 2', 'Under 3', 'Under 4', 'Under 5', 'Under 6', 'Under 7', 'Under 8', 'Under 9', 'Under 10',
      'Over 1', 'Over 2', 'Over 3', 'Over 4', 'Over 5', 'Over 6', 'Over 7', 'Over 8', 'Over 9', 'Over 10', 'Over 20', 'Over 30', 'Over 40', 'Over 50',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter Price/Cash ratio',
    validation: 'number',
  },
  {
    id: 'fs_fa_pfcf',
    label: 'Price/FCF Ratio',
    type: 'select',
    options: createOptions([
      'Under 5', 'Under 10', 'Under 15', 'Under 20', 'Under 25', 'Under 30', 'Under 35', 'Under 40', 'Under 45', 'Under 50', 'Under 60', 'Under 70', 'Under 80', 'Under 90', 'Under 100',
      'Over 5', 'Over 10', 'Over 15', 'Over 20', 'Over 25', 'Over 30', 'Over 35', 'Over 40', 'Over 45', 'Over 50', 'Over 60', 'Over 70', 'Over 80', 'Over 90', 'Over 100',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter Price/FCF ratio',
    validation: 'number',
  },
  {
    id: 'fs_fa_evsales',
    label: 'EV/Sales',
    type: 'select',
    options: createOptions(['<1', '<2', '<3', '<5', '<10', '<15', '>1', '>2', '>3', '>5', '>10', '>15']),
    allowCustom: true,
    customPlaceholder: 'Enter EV/Sales',
    validation: 'number',
  },
  {
    id: 'fs_fa_evebitda',
    label: 'EV/EBITDA',
    type: 'select',
    options: createOptions(['<5', '<10', '<15', '<20', '<25', '<30', '>5', '>10', '>15', '>20', '>25', '>30']),
    allowCustom: true,
    customPlaceholder: 'Enter EV/EBITDA',
    validation: 'number',
  },
  {
    id: 'fs_fa_div',
    label: 'Dividend Yield',
    type: 'select',
    options: createOptions([
      'None (0%)',
      'Positive (>0%)',
      'Over 1%', 'Over 2%', 'Over 3%', 'Over 4%', 'Over 5%', 'Over 6%', 'Over 7%', 'Over 8%', 'Over 9%', 'Over 10%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter dividend yield (e.g., Over 2%)',
    validation: 'percent',
  },
  {
    id: 'fs_fa_divgrowth',
    label: 'Dividend Growth',
    type: 'select',
    options: createOptions(['Positive', 'Negative', '>0%', '>10%', '>25%', '>50%', '>100%']),
    allowCustom: true,
    customPlaceholder: 'Enter dividend growth %',
    validation: 'percent',
  },
  {
    id: 'fs_fa_epsyoy',
    label: 'EPS Growth (This Year)',
    type: 'select',
    options: createOptions([
      'Negative (<0%)', 'Positive (>0%)',
      'Under 5%', 'Under 10%', 'Under 15%', 'Under 20%', 'Under 25%', 'Under 30%',
      'Over 5%', 'Over 10%', 'Over 15%', 'Over 20%', 'Over 25%', 'Over 30%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter EPS growth %',
    validation: 'percent',
  },
  {
    id: 'fs_fa_epsyoy1',
    label: 'EPS Growth (Next Year)',
    type: 'select',
    options: createOptions([
      'Negative (<0%)', 'Positive (>0%)',
      'Under 5%', 'Under 10%', 'Under 15%', 'Under 20%', 'Under 25%', 'Under 30%',
      'Over 5%', 'Over 10%', 'Over 15%', 'Over 20%', 'Over 25%', 'Over 30%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter next year EPS growth %',
    validation: 'percent',
  },
  {
    id: 'fs_fa_epsqoq',
    label: 'EPS Growth (QoQ)',
    type: 'select',
    options: createOptions([
      'Negative (<0%)', 'Positive (>0%)',
      'Under 5%', 'Under 10%', 'Under 15%', 'Under 20%', 'Under 25%', 'Under 30%',
      'Over 5%', 'Over 10%', 'Over 15%', 'Over 20%', 'Over 25%', 'Over 30%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter QoQ EPS growth %',
    validation: 'percent',
  },
  {
    id: 'fs_fa_eps3years',
    label: 'EPS Growth (3Y)',
    type: 'select',
    options: createOptions([
      'Negative (<0%)', 'Positive (>0%)',
      'Under 5%', 'Under 10%', 'Under 15%', 'Under 20%', 'Under 25%', 'Under 30%',
      'Over 5%', 'Over 10%', 'Over 15%', 'Over 20%', 'Over 25%', 'Over 30%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter 3Y EPS growth %',
    validation: 'percent',
  },
  {
    id: 'fs_fa_estltgrowth',
    label: 'EPS Growth (5Y Estimate)',
    type: 'select',
    options: createOptions([
      'Negative (<0%)', 'Positive (>0%)',
      'Under 5%', 'Under 10%', 'Under 15%', 'Under 20%', 'Under 25%', 'Under 30%',
      'Over 5%', 'Over 10%', 'Over 15%', 'Over 20%', 'Over 25%', 'Over 30%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter 5Y estimated EPS growth %',
    validation: 'percent',
  },
  {
    id: 'fs_fa_roa',
    label: 'Return on Assets',
    type: 'select',
    options: createOptions([
      'Positive (>0%)', 'Negative (<0%)',
      'Under -50%', 'Under -40%', 'Under -30%', 'Under -20%', 'Under -10%', 'Under -5%',
      'Over +5%', 'Over +10%', 'Over +15%', 'Over +20%', 'Over +25%', 'Over +30%', 'Over +35%', 'Over +40%', 'Over +45%', 'Over +50%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter ROA %',
    validation: 'percent',
  },
  {
    id: 'fs_fa_roe',
    label: 'Return on Equity',
    type: 'select',
    options: createOptions([
      'Positive (>0%)', 'Negative (<0%)',
      'Under -50%', 'Under -40%', 'Under -30%', 'Under -20%', 'Under -10%', 'Under -5%',
      'Over +5%', 'Over +10%', 'Over +15%', 'Over +20%', 'Over +25%', 'Over +30%', 'Over +35%', 'Over +40%', 'Over +45%', 'Over +50%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter ROE %',
    validation: 'percent',
  },
  {
    id: 'fs_fa_roi',
    label: 'Return on Invested Capital',
    type: 'select',
    options: createOptions([
      'Positive (>0%)', 'Negative (<0%)',
      'Under -50%', 'Under -40%', 'Under -30%', 'Under -20%', 'Under -10%', 'Under -5%',
      'Over +5%', 'Over +10%', 'Over +15%', 'Over +20%', 'Over +25%', 'Over +30%', 'Over +35%', 'Over +40%', 'Over +45%', 'Over +50%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter ROIC %',
    validation: 'percent',
  },
  {
    id: 'fs_fa_grossmargin',
    label: 'Gross Margin',
    type: 'select',
    options: createOptions([
      'Positive (>0%)', 'Negative (<0%)',
      'Under 0%', 'Under 5%', 'Under 10%', 'Under 15%', 'Under 20%', 'Under 25%', 'Under 30%', 'Under 35%', 'Under 40%', 'Under 45%', 'Under 50%', 'Under 60%', 'Under 70%', 'Under 80%', 'Under 90%',
      'Over 0%', 'Over 5%', 'Over 10%', 'Over 15%', 'Over 20%', 'Over 25%', 'Over 30%', 'Over 35%', 'Over 40%', 'Over 45%', 'Over 50%', 'Over 60%', 'Over 70%', 'Over 80%', 'Over 90%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter gross margin %',
    validation: 'percent',
  },
  {
    id: 'fs_fa_opermargin',
    label: 'Operating Margin',
    type: 'select',
    options: createOptions([
      'Positive (>0%)', 'Negative (<0%)',
      'Under 0%', 'Under 5%', 'Under 10%', 'Under 15%', 'Under 20%', 'Under 25%', 'Under 30%', 'Under 35%', 'Under 40%', 'Under 45%', 'Under 50%', 'Under 60%', 'Under 70%', 'Under 80%', 'Under 90%',
      'Over 0%', 'Over 5%', 'Over 10%', 'Over 15%', 'Over 20%', 'Over 25%', 'Over 30%', 'Over 35%', 'Over 40%', 'Over 45%', 'Over 50%', 'Over 60%', 'Over 70%', 'Over 80%', 'Over 90%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter operating margin %',
    validation: 'percent',
  },
  {
    id: 'fs_fa_netmargin',
    label: 'Net Profit Margin',
    type: 'select',
    options: createOptions([
      'Positive (>0%)', 'Negative (<0%)',
      'Under 0%', 'Under 5%', 'Under 10%', 'Under 15%', 'Under 20%', 'Under 25%', 'Under 30%', 'Under 35%', 'Under 40%', 'Under 45%', 'Under 50%', 'Under 60%', 'Under 70%', 'Under 80%', 'Under 90%',
      'Over 0%', 'Over 5%', 'Over 10%', 'Over 15%', 'Over 20%', 'Over 25%', 'Over 30%', 'Over 35%', 'Over 40%', 'Over 45%', 'Over 50%', 'Over 60%', 'Over 70%', 'Over 80%', 'Over 90%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter net margin %',
    validation: 'percent',
  },
  {
    id: 'fs_fa_payoutratio',
    label: 'Payout Ratio',
    type: 'select',
    options: createOptions([
      'None (0%)',
      'Under 10%', 'Under 20%', 'Under 30%', 'Under 40%', 'Under 50%', 'Under 60%', 'Under 70%', 'Under 80%', 'Under 90%', 'Under 100%',
      'Over 10%', 'Over 20%', 'Over 30%', 'Over 40%', 'Over 50%', 'Over 60%', 'Over 70%', 'Over 80%', 'Over 90%', 'Over 100%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter payout ratio %',
    validation: 'percent',
  },
  {
    id: 'fs_fa_curratio',
    label: 'Current Ratio',
    type: 'select',
    options: createOptions([
      'Under 0.5', 'Under 1',
      'Over 0.5', 'Over 1', 'Over 1.5', 'Over 2', 'Over 3', 'Over 4', 'Over 5', 'Over 10',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter current ratio',
    validation: 'number',
  },
  {
    id: 'fs_fa_quickratio',
    label: 'Quick Ratio',
    type: 'select',
    options: createOptions([
      'Under 0.5', 'Under 1',
      'Over 0.5', 'Over 1', 'Over 1.5', 'Over 2', 'Over 3', 'Over 4', 'Over 5', 'Over 10',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter quick ratio',
    validation: 'number',
  },
  {
    id: 'fs_fa_ltdebteq',
    label: 'LT Debt/Equity',
    type: 'select',
    options: createOptions([
      'Under 0.1', 'Under 0.2', 'Under 0.3', 'Under 0.4', 'Under 0.5', 'Under 0.6', 'Under 0.7', 'Under 0.8', 'Under 0.9', 'Under 1',
      'Over 0.1', 'Over 0.2', 'Over 0.3', 'Over 0.4', 'Over 0.5', 'Over 0.6', 'Over 0.7', 'Over 0.8', 'Over 0.9', 'Over 1',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter LT debt/equity ratio',
    validation: 'number',
  },
  {
    id: 'fs_fa_debteq',
    label: 'Debt/Equity',
    type: 'select',
    options: createOptions([
      'Under 0.1', 'Under 0.2', 'Under 0.3', 'Under 0.4', 'Under 0.5', 'Under 0.6', 'Under 0.7', 'Under 0.8', 'Under 0.9', 'Under 1',
      'Over 0.1', 'Over 0.2', 'Over 0.3', 'Over 0.4', 'Over 0.5', 'Over 0.6', 'Over 0.7', 'Over 0.8', 'Over 0.9', 'Over 1',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter debt/equity ratio',
    validation: 'number',
  },
];

// Ownership Tab Filters (fs_sh_* for Share/Ownership, fs_an_* for Analyst)
export const ownershipFilters: FinvizFilterField[] = [
  {
    id: 'fs_sh_insiderown',
    label: 'Insider Ownership',
    type: 'select',
    options: createOptions([
      'Under 1%', 'Under 5%', 'Under 10%', 'Under 15%', 'Under 20%', 'Under 25%', 'Under 30%', 'Under 35%', 'Under 40%', 'Under 45%', 'Under 50%',
      'Over 1%', 'Over 5%', 'Over 10%', 'Over 15%', 'Over 20%', 'Over 25%', 'Over 30%', 'Over 35%', 'Over 40%', 'Over 45%', 'Over 50%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter insider ownership %',
    validation: 'percent',
  },
  {
    id: 'fs_sh_insidertrans',
    label: 'Insider Transactions',
    type: 'select',
    options: createOptions([
      'Under -5%', 'Under -10%', 'Under -25%', 'Under -50%', 'Under -100%', 'Under -500%', 'Under -1000%',
      'Over 5%', 'Over 10%', 'Over 25%', 'Over 50%', 'Over 100%', 'Over 500%', 'Over 1000%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter insider transaction %',
    validation: 'percent',
  },
  {
    id: 'fs_sh_instown',
    label: 'Institutional Ownership',
    type: 'select',
    options: createOptions([
      'Under 1%', 'Under 5%', 'Under 10%', 'Under 15%', 'Under 20%', 'Under 25%', 'Under 30%', 'Under 35%', 'Under 40%', 'Under 45%', 'Under 50%', 'Under 55%', 'Under 60%', 'Under 65%', 'Under 70%', 'Under 75%', 'Under 80%', 'Under 85%', 'Under 90%',
      'Over 1%', 'Over 5%', 'Over 10%', 'Over 15%', 'Over 20%', 'Over 25%', 'Over 30%', 'Over 35%', 'Over 40%', 'Over 45%', 'Over 50%', 'Over 55%', 'Over 60%', 'Over 65%', 'Over 70%', 'Over 75%', 'Over 80%', 'Over 85%', 'Over 90%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter institutional ownership %',
    validation: 'percent',
  },
  {
    id: 'fs_sh_insttrans',
    label: 'Institutional Transactions',
    type: 'select',
    options: createOptions([
      'Under -5%', 'Under -10%', 'Under -25%', 'Under -50%', 'Under -100%', 'Under -500%',
      'Over 5%', 'Over 10%', 'Over 25%', 'Over 50%', 'Over 100%', 'Over 500%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter institutional transaction %',
    validation: 'percent',
  },
  {
    id: 'fs_sh_short',
    label: 'Short Float / Interest',
    type: 'select',
    options: createOptions([
      'Under 1%', 'Under 5%', 'Under 10%', 'Under 15%', 'Under 20%', 'Under 25%', 'Under 30%', 'Under 35%', 'Under 40%',
      'Over 1%', 'Over 5%', 'Over 10%', 'Over 15%', 'Over 20%', 'Over 25%', 'Over 30%', 'Over 35%', 'Over 40%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter short float %',
    validation: 'percent',
  },
  {
    id: 'fs_an_recom',
    label: 'Analyst Recommendation',
    type: 'select',
    options: createOptions([
      'Strong Buy (1)', 'Buy', 'Hold', 'Sell', 'Strong Sell (5)',
    ]),
    allowCustom: true,
  },
  {
    id: 'fs_sh_opt',
    label: 'Optionable',
    type: 'select',
    options: createOptions(['Yes', 'No']),
    allowCustom: true,
  },
  {
    id: 'fs_earningsdate',
    label: 'Earnings Date',
    type: 'select',
    options: createOptions(['Today', 'Tomorrow', 'This Week', 'Next Week', 'This Month', 'Next Month']),
    allowCustom: true,
  },
];

// Performance Tab Filters (fs_ta_* for Technical Analysis)
export const performanceFilters: FinvizFilterField[] = [
  {
    id: 'fs_ta_perf',
    label: 'Performance',
    type: 'select',
    options: performanceOptions,
    allowCustom: true,
    customPlaceholder: 'Enter performance %',
    validation: 'percent',
    note: '131 options including weekly/monthly/quarterly/ytd/yearly performance',
  },
  {
    id: 'fs_ta_perf2',
    label: 'Performance 2',
    type: 'select',
    options: performanceOptions,
    allowCustom: true,
    customPlaceholder: 'Enter performance 2 %',
    validation: 'percent',
  },
  {
    id: 'fs_ta_volatility',
    label: 'Volatility',
    type: 'select',
    options: createOptions(['Low', 'Medium', 'High', '<5%', '<10%', '<15%', '<20%', '>5%', '>10%', '>15%', '>20%']),
    allowCustom: true,
    customPlaceholder: 'Enter volatility %',
    validation: 'percent',
  },
  {
    id: 'fs_ta_rsi',
    label: 'RSI (14)',
    type: 'select',
    options: createOptions([
      // Must match finvizfinance Overview filter_dict["RSI (14)"] option keys exactly
      'Overbought (90)',
      'Overbought (80)',
      'Overbought (70)',
      'Overbought (60)',
      'Oversold (40)',
      'Oversold (30)',
      'Oversold (20)',
      'Oversold (10)',
      'Not Overbought (<60)',
      'Not Overbought (<50)',
      'Not Oversold (>50)',
      'Not Oversold (>40)',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter RSI value (e.g., Not Oversold (>30))',
    validation: 'number',
  },
  {
    id: 'fs_ta_gap',
    label: 'Gap',
    type: 'select',
    options: createOptions([
      'Up 1%', 'Up 2%', 'Up 3%', 'Up 4%', 'Up 5%', 'Up 6%', 'Up 7%', 'Up 8%', 'Up 9%', 'Up 10%', 'Up 15%', 'Up 20%',
      'Down 1%', 'Down 2%', 'Down 3%', 'Down 4%', 'Down 5%', 'Down 6%', 'Down 7%', 'Down 8%', 'Down 9%', 'Down 10%', 'Down 15%', 'Down 20%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter gap %',
    validation: 'percent',
  },
  {
    id: 'fs_ta_sma20',
    label: '20-Day SMA',
    type: 'select',
    options: createOptions([
      'Price above SMA20', 'Price below SMA20', 'Price crossed SMA20 above', 'Price crossed SMA20 below',
    ]),
    allowCustom: true,
  },
  {
    id: 'fs_ta_sma50',
    label: '50-Day SMA',
    type: 'select',
    options: createOptions([
      'Price above SMA50', 'Price below SMA50', 'Price crossed SMA50 above', 'Price crossed SMA50 below',
    ]),
    allowCustom: true,
  },
  {
    id: 'fs_ta_sma200',
    label: '200-Day SMA',
    type: 'select',
    options: createOptions([
      'Price above SMA200', 'Price below SMA200', 'Price crossed SMA200 above', 'Price crossed SMA200 below',
    ]),
    allowCustom: true,
  },
  {
    id: 'fs_ta_change',
    label: 'Change',
    type: 'select',
    options: createOptions([
      'Up 1%', 'Up 2%', 'Up 3%', 'Up 4%', 'Up 5%', 'Up 6%', 'Up 7%', 'Up 8%', 'Up 9%', 'Up 10%', 'Up 15%', 'Up 20%',
      'Down 1%', 'Down 2%', 'Down 3%', 'Down 4%', 'Down 5%', 'Down 6%', 'Down 7%', 'Down 8%', 'Down 9%', 'Down 10%', 'Down 15%', 'Down 20%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter change %',
    validation: 'percent',
  },
  {
    id: 'fs_ta_changeopen',
    label: 'Change from Open',
    type: 'select',
    options: createOptions([
      'Up 1%', 'Up 2%', 'Up 3%', 'Up 4%', 'Up 5%', 'Up 6%', 'Up 7%', 'Up 8%', 'Up 9%', 'Up 10%',
      'Down 1%', 'Down 2%', 'Down 3%', 'Down 4%', 'Down 5%', 'Down 6%', 'Down 7%', 'Down 8%', 'Down 9%', 'Down 10%',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter change from open %',
    validation: 'percent',
  },
  {
    id: 'fs_ta_highlow20d',
    label: '20-Day High/Low',
    type: 'select',
    options: createOptions([
      'New High', 'New Low', 'Above High', 'Below Low',
      'Within 1% of High', 'Within 2% of High', 'Within 3% of High', 'Within 5% of High', 'Within 10% of High', 'Within 15% of High', 'Within 20% of High',
      'Within 1% of Low', 'Within 2% of Low', 'Within 3% of Low', 'Within 5% of Low', 'Within 10% of Low', 'Within 15% of Low', 'Within 20% of Low',
    ]),
    allowCustom: true,
  },
  {
    id: 'fs_ta_highlow50d',
    label: '50-Day High/Low',
    type: 'select',
    options: createOptions([
      'New High', 'New Low', 'Above High', 'Below Low',
      'Within 1% of High', 'Within 2% of High', 'Within 3% of High', 'Within 5% of High', 'Within 10% of High', 'Within 15% of High', 'Within 20% of High',
      'Within 1% of Low', 'Within 2% of Low', 'Within 3% of Low', 'Within 5% of Low', 'Within 10% of Low', 'Within 15% of Low', 'Within 20% of Low',
    ]),
    allowCustom: true,
  },
  {
    id: 'fs_ta_highlow52w',
    label: '52-Week High/Low',
    type: 'select',
    options: createOptions([
      'New High', 'New Low', 'Above High', 'Below Low',
      'Within 1% of High', 'Within 2% of High', 'Within 3% of High', 'Within 5% of High', 'Within 10% of High', 'Within 15% of High', 'Within 20% of High', 'Within 25% of High', 'Within 30% of High',
      'Within 1% of Low', 'Within 2% of Low', 'Within 3% of Low', 'Within 5% of Low', 'Within 10% of Low', 'Within 15% of Low', 'Within 20% of Low', 'Within 25% of Low', 'Within 30% of Low',
    ]),
    allowCustom: true,
  },
  {
    id: 'fs_ta_pattern',
    label: 'Pattern',
    type: 'select',
    options: createOptions([
      'Doji',
      'Hammer',
      'Inverted Hammer',
      'Engulfing',
      'Morning Star',
      'Evening Star',
      'Three White Soldiers',
      'Three Black Crows',
      'Double Top',
      'Double Bottom',
      'Head and Shoulders',
      'Cup and Handle',
      'Wedge',
      'Triangle',
      'Channel',
    ]),
    allowCustom: true,
  },
  {
    id: 'fs_ta_beta',
    label: 'Beta',
    type: 'select',
    options: createOptions([
      'Under 0.25', 'Under 0.5', 'Under 0.75', 'Under 1', 'Under 1.25', 'Under 1.5', 'Under 1.75', 'Under 2', 'Under 2.5', 'Under 3',
      'Over 0.25', 'Over 0.5', 'Over 0.75', 'Over 1', 'Over 1.25', 'Over 1.5', 'Over 1.75', 'Over 2', 'Over 2.5', 'Over 3',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter beta value',
    validation: 'number',
  },
];

// Technical Tab Filters (Volume, Price, etc.)
export const technicalFilters: FinvizFilterField[] = [
  {
    id: 'fs_sh_avgvol',
    label: 'Average Volume',
    type: 'select',
    options: createOptions([
      'Under 100K', '100K to 500K', '500K to 1M', '1M to 5M', '5M to 10M', 'Over 10M',
      'Under 200K', '200K to 500K', '500K to 1M', '1M to 2M', '2M to 5M', '5M to 10M', 'Over 10M', 'Over 20M', 'Over 50M', 'Over 100M',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter average volume',
    validation: 'number',
  },
  {
    id: 'fs_sh_relvol',
    label: 'Relative Volume',
    type: 'select',
    options: createOptions([
      'Under 0.5', '0.5 to 0.75', '0.75 to 1', '1 to 1.5', '1.5 to 2', '2 to 3', '3 to 5', 'Over 5', 'Over 10',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter relative volume',
    validation: 'number',
  },
  {
    id: 'fs_sh_curvol',
    label: 'Current Volume',
    type: 'select',
    options: createOptions([
      'Under 100K', '100K to 500K', '500K to 1M', '1M to 5M', '5M to 10M', 'Over 10M',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter current volume',
    validation: 'number',
  },
  {
    id: 'fs_sh_price',
    label: 'Price',
    type: 'select',
    options: createOptions([
      'Under $1', '$1 to $2', '$2 to $3', '$3 to $4', '$4 to $5',
      '$5 to $10', '$10 to $20', '$20 to $50', '$50 to $100', '$100 to $200',
      'Over $200', 'Over $300', 'Over $400', 'Over $500',
      'Under $5', 'Under $10', 'Under $20', 'Under $30', 'Under $40', 'Under $50',
    ]),
    allowCustom: true,
    customPlaceholder: 'Enter price range (e.g., $10 to $50)',
    validation: 'currency',
  },
  {
    id: 'fs_targetprice',
    label: 'vs Analyst Target',
    type: 'select',
    options: createOptions(['Above Target', 'Below Target', 'Within 5%', 'Within 10%', 'Within 20%', 'Within 30%']),
    allowCustom: true,
  },
  {
    id: 'fs_sh_outstanding',
    label: 'Shares Outstanding',
    type: 'select',
    options: createOptions(['<1M', '1M-10M', '10M-50M', '50M-100M', '100M-500M', '>500M']),
    allowCustom: true,
    customPlaceholder: 'Enter shares outstanding',
    validation: 'number',
  },
  {
    id: 'fs_sh_float',
    label: 'Float',
    type: 'select',
    options: createOptions(['<1M', '1M-10M', '10M-50M', '50M-100M', '100M-500M', '>500M']),
    allowCustom: true,
    customPlaceholder: 'Enter float',
    validation: 'number',
  },
  {
    id: 'fs_ipodate',
    label: 'IPO Date',
    type: 'select',
    options: createOptions(['<1 Year Ago', '<3 Years Ago', '<5 Years Ago', '<10 Years Ago', '>10 Years Ago']),
    allowCustom: true,
  },
];

// ETF Tab Filters (fs_etf_* prefix)
export const etfFilters: FinvizFilterField[] = [
  {
    id: 'fs_etf_category',
    label: 'ETF Category',
    type: 'select',
    options: createOptions([
      'Broad ETFs',
      'Sector ETFs',
      'Bond ETFs',
      'Commodity ETFs',
      'Currency ETFs',
      'Inverse ETFs',
      'Leveraged ETFs',
    ]),
    allowCustom: true,
  },
  {
    id: 'fs_etf_assettype',
    label: 'ETF Asset Type',
    type: 'select',
    options: createOptions([
      'Stocks',
      'Bonds',
      'Commodities',
      'Currencies',
      'Crypto',
      'Real Estate',
    ]),
    allowCustom: true,
  },
  {
    id: 'fs_etf_netexpense',
    label: 'Net Expense Ratio',
    type: 'select',
    options: createOptions(['<0.1%', '<0.25%', '<0.5%', '<0.75%', '<1%', '>0.1%', '>0.25%', '>0.5%', '>0.75%', '>1%']),
    allowCustom: true,
    customPlaceholder: 'Enter expense ratio %',
    validation: 'percent',
  },
  {
    id: 'fs_etf_fundflows',
    label: 'Net Fund Flows',
    type: 'select',
    options: createOptions(['>1M', '>5M', '>10M', '>25M', '>50M', '>100M', '>500M']),
    allowCustom: true,
    customPlaceholder: 'Enter fund flows',
    validation: 'number',
  },
  {
    id: 'fs_etf_return',
    label: 'ETF Annualized Return',
    type: 'select',
    options: performanceOptions,
    allowCustom: true,
    customPlaceholder: 'Enter return %',
    validation: 'percent',
  },
];

/** All filter fields (for label lookup across tabs) */
export const ALL_FINVIZ_FILTER_FIELDS: FinvizFilterField[] = [
  ...descriptiveFilters,
  ...valuationFilters,
  ...ownershipFilters,
  ...performanceFilters,
  ...technicalFilters,
  ...etfFilters,
];

const FINVIZ_FIELD_BY_ID = new Map<string, FinvizFilterField>(
  ALL_FINVIZ_FILTER_FIELDS.map((f) => [f.id, f])
);

export function getFinvizFilterFieldById(id: string): FinvizFilterField | undefined {
  return FINVIZ_FIELD_BY_ID.get(id);
}

// Get filters by tab
export const getFiltersByTab = (tabId: FinvizTabId): FinvizFilterField[] => {
  switch (tabId) {
    case 'descriptive':
      return descriptiveFilters;
    case 'valuation':
      return valuationFilters;
    case 'ownership':
      return ownershipFilters;
    case 'performance':
      return performanceFilters;
    case 'technical':
      return technicalFilters;
    case 'etf':
      return etfFilters;
    default:
      return [];
  }
};

// Get tab label
export const getTabLabel = (tabId: FinvizTabId): string => {
  const tab = FINVIZ_TABS.find(t => t.id === tabId);
  return tab?.label || '';
};
